/**
 * @file     useRentVsBuyState.ts
 * @purpose  React state hook that owns the Pro rent vs buy inputs, derives results via rentVsBuyMath, and exposes patch helpers. Seeds defaults from the visitor profile, fires analytics on input changes, and writes the verdict + break-even year back to the profile.
 */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  computeRentVsBuy,
  defaultRentVsBuyInputs,
  type RentVsBuyInputs,
  type RentVsBuyResult,
} from './rentVsBuyMath';
import { useToolProfileBridge } from '../_shared/useToolProfileBridge';
import { PROFILE_KEYS, PUBLIC_KEYS, stayDurationToYears } from '@/core/config/profileKeys';

const RESULT_WRITE_DEBOUNCE_MS = 1200;

export type UseRentVsBuyState = {
  inputs: RentVsBuyInputs;
  result: RentVsBuyResult;
  patch: (next: Partial<RentVsBuyInputs>) => void;
  reset: () => void;
};

export function useRentVsBuyState(): UseRentVsBuyState {
  const bridge = useToolProfileBridge('rent-vs-buy');
  const seededRef = useRef(false);
  const lastVerdictRef = useRef<'buy' | 'rent' | null>(null);

  const [inputs, setInputs] = useState<RentVsBuyInputs>(() => defaultRentVsBuyInputs());

  // One-shot seed from the visitor profile.
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    setInputs((prev) => {
      const explicitPrice = bridge.readSeedWithFallback(
        PROFILE_KEYS.targetPrice,
        PUBLIC_KEYS.estimatedBudget,
        -1,
      );
      const balance = bridge.readSeed(PROFILE_KEYS.mortgageBalance, -1);
      const homePrice = explicitPrice > 0
        ? explicitPrice
        : balance > 0
          ? Math.round(balance / 0.8)
          : prev.homePrice;
      const explicitDown = bridge.readSeed(PROFILE_KEYS.downPaymentSavings, -1);
      const dpPct = bridge.readSeedWithFallback(
        PROFILE_KEYS.downPaymentSavings,
        PUBLIC_KEYS.downPaymentPercent,
        0,
      );
      const downPayment = explicitDown > 0
        ? explicitDown
        : dpPct > 0
          ? Math.round((homePrice * dpPct) / 100)
          : prev.downPayment;
      // Re-derive tax + closing-costs Greater Vancouver defaults when the
      // seeded price changed.
      const priceChanged = homePrice !== prev.homePrice;
      const propertyTaxAnnual = priceChanged ? Math.round(homePrice * 0.003) : prev.propertyTaxAnnual;
      const closingCosts = priceChanged ? Math.round(homePrice * 0.0275) : prev.closingCosts;
      return {
        ...prev,
        homePrice,
        downPayment,
        propertyTaxAnnual,
        closingCosts,
        mortgageRatePct: bridge.readSeed(PROFILE_KEYS.mortgageRate, prev.mortgageRatePct),
        amortizationYears: bridge.readSeed(PROFILE_KEYS.amortizationYears, prev.amortizationYears),
        rentMonthly: bridge.readSeedWithFallback(
          PROFILE_KEYS.monthlyRent,
          PUBLIC_KEYS.currentRent,
          prev.rentMonthly,
        ),
        horizonYears: bridge.readSeedWithFallback(
          PROFILE_KEYS.timeHorizonYears,
          PUBLIC_KEYS.plannedStayDuration,
          prev.horizonYears,
          (raw) => (typeof raw === 'string' ? stayDurationToYears(raw) : null),
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = useCallback((next: Partial<RentVsBuyInputs>) => {
    setInputs((prev) => ({ ...prev, ...next }));
    Object.keys(next).forEach((k) => bridge.fireInputChanged(k));
  }, [bridge]);

  const reset = useCallback(() => {
    setInputs(defaultRentVsBuyInputs());
    bridge.fireEvent('tool_action', { action: 'reset' });
  }, [bridge]);

  const result = useMemo(() => computeRentVsBuy(inputs), [inputs]);

  // Debounced write-back of cross-tool seeds + tool-specific verdict.
  useEffect(() => {
    const t = setTimeout(() => {
      bridge.writeSeed(PROFILE_KEYS.targetPrice, inputs.homePrice);
      bridge.writeSeed(PROFILE_KEYS.downPaymentSavings, inputs.downPayment);
      bridge.writeSeed(PROFILE_KEYS.mortgageRate, inputs.mortgageRatePct);
      bridge.writeSeed(PROFILE_KEYS.amortizationYears, inputs.amortizationYears);
      bridge.writeSeed(PROFILE_KEYS.monthlyRent, inputs.rentMonthly);
      bridge.writeSeed(PROFILE_KEYS.timeHorizonYears, inputs.horizonYears);
      // Loan principal so budget safety opens with the right balance.
      // Rent vs buy doesn't model CMHC; price - down is good enough.
      bridge.writeSeed(
        PROFILE_KEYS.mortgageBalance,
        Math.max(0, Math.round(inputs.homePrice - inputs.downPayment)),
      );
      // Mirror to legacy public keys so the public mortgage calculator and
      // rent-vs-buy interactive remember the user too.
      bridge.writePublicSeed(PUBLIC_KEYS.estimatedBudget, inputs.homePrice);
      if (inputs.homePrice > 0) {
        bridge.writePublicSeed(
          PUBLIC_KEYS.downPaymentPercent,
          Math.round((inputs.downPayment / inputs.homePrice) * 1000) / 10,
        );
      }
      bridge.writePublicSeed(PUBLIC_KEYS.currentRent, inputs.rentMonthly);

      const verdict: 'buy' | 'rent' = result.buyerWins ? 'buy' : 'rent';
      bridge.writeSeed(PROFILE_KEYS.rentVsBuyVerdict, verdict);
      bridge.writeSeed(
        PROFILE_KEYS.rentVsBuyBreakEvenYear,
        result.breakEvenYear ?? -1,
      );
      if (lastVerdictRef.current && lastVerdictRef.current !== verdict) {
        bridge.fireEvent('tool_milestone', {
          milestone: 'verdict_changed',
          from: lastVerdictRef.current,
          to: verdict,
          breakEvenYear: result.breakEvenYear,
        });
      }
      lastVerdictRef.current = verdict;
    }, RESULT_WRITE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputs, result.buyerWins, result.breakEvenYear, bridge]);

  return { inputs, result, patch, reset };
}
